import { TFunction } from "i18next";
import { Link } from "react-router-dom";

import { EventTypeCellParts, EventTypeStrategy } from "./EventTypeStrategy";
import { getTranslatedEventTypeName } from "../../utils/eventTypeUtils";

import { Button } from "@/components/ui/v2/button";
import { TransactionType } from "@/types/transaction/TransactionType";
import { formatCurrency } from "@/utils/valueFormat";

export class CampaignInvoiceUpdatedEventTypeStrategy
implements EventTypeStrategy
{
  renderCell(row: TransactionType, t: TFunction): JSX.Element {
    const forText = row.eventData.campaignName ? (
      <>
        {" "}
        {t("for")} <b>{row.eventData.campaignName}</b>
      </>
    ) : (
      ""
    );
    const onText = row.eventData.jobBoardName ? (
      <>
        {" "}
        {t("on")} <b>{row.eventData.jobBoardName}</b>
      </>
    ) : (
      ""
    );
    const inText = row.eventData.month ? (
      <>
        {" "}
        {t("in")}{" "}
        <b>
          {new Date(row.eventData.month).toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          })}
        </b>
      </>
    ) : (
      ""
    );
    const previousAmountText = row.eventData.previousAmount ? (
      <>
        {" | "}
        {t("common.previous")} {t("common.amount")}:{" "}
        <b>{formatCurrency(row.eventData.previousAmount)}</b>
      </>
    ) : (
      ""
    );

    return (
      <>
        {t(`eventTypes.${row.eventType}`)}
        {forText}
        {onText}
        {inText}
        {previousAmountText}
      </>
    );
  }

  getTypeAndDescription(
    row: TransactionType,
    t: TFunction
  ): EventTypeCellParts {
    const campaignName = row.eventData.campaignName || "";
    const jobBoardName = row.eventData.jobBoardName || "";
    const campaignId = row.eventData.campaignId;
    const clientIntegrationModuleId =
      row.eventData.clientIntegrationModuleId ?? row.eventData.integrationModuleId ?? row.eventData.jobBoardId;
    const clientId = row.eventData.clientId || row.clientId2;
    const month = row.eventData.month;
    const previousAmount = row.eventData.previousAmount;

    const parts: (string | JSX.Element)[] = [];

    // "for [Campaign Name (link)]" - Show if we have campaignName OR campaignId
    if (campaignName || campaignId) {
      if (parts.length > 0) parts.push(" ");
      if (campaignName && campaignId && clientId) {
        // Full link with campaign name
        parts.push(
          <>
            {t("common.for")}{" "}
            <Button asChild variant={"link"} className="px-0">
              <Link to={`/clients/${clientId}/campaigns/${campaignId}`}>
                {campaignName}
              </Link>
            </Button>
          </>
        );
      } else if (campaignName) {
        // Just campaign name without link
        parts.push(
          <>
            {t("common.for")} <b>{campaignName}</b>
          </>
        );
      } else if (campaignId && clientId) {
        // Campaign ID as link if name is missing
        parts.push(
          <>
            {t("common.for")}{" "}
            <Button asChild variant={"link"} className="px-0">
              <Link to={`/clients/${clientId}/campaigns/${campaignId}`}>
                {campaignId}
              </Link>
            </Button>
          </>
        );
      } else if (campaignId) {
        // Just campaign ID
        parts.push(
          <>
            {t("common.for")} <b>{campaignId}</b>
          </>
        );
      }
    }

    // "on [Job Board Name (link)]" - Show if we have jobBoardName OR clientIntegrationModuleId
    if (jobBoardName || clientIntegrationModuleId) {
      if (parts.length > 0) parts.push(" ");
      if (jobBoardName && clientIntegrationModuleId && campaignId && clientId) {
        // Full link with job board name
        parts.push(
          <>
            {t("common.on")}{" "}
            <Button asChild variant={"link"} className="px-0">
              <Link
                to={`/clients/${clientId}/campaigns/${campaignId}/integrations/${clientIntegrationModuleId}`}
              >
                {jobBoardName}
              </Link>
            </Button>
          </>
        );
      } else if (jobBoardName) {
        // Just job board name without link
        parts.push(
          <>
            {t("common.on")} <b>{jobBoardName}</b>
          </>
        );
      } else if (clientIntegrationModuleId && campaignId && clientId) {
        // Integration module ID as link if name is missing
        parts.push(
          <>
            {t("common.on")}{" "}
            <Button asChild variant={"link"} className="px-0">
              <Link
                to={`/clients/${clientId}/campaigns/${campaignId}/integrations/${clientIntegrationModuleId}`}
              >
                {clientIntegrationModuleId}
              </Link>
            </Button>
          </>
        );
      } else if (clientIntegrationModuleId) {
        // Just integration module ID
        parts.push(
          <>
            {t("common.on")} <b>{clientIntegrationModuleId}</b>
          </>
        );
      }
    }

    // "in [Month Year]"
    if (month) {
      if (parts.length > 0) parts.push(" ");
      parts.push(
        <>
          {t("common.in")}{" "}
          <b>
            {new Date(month).toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </b>
        </>
      );
    }

    // Show previous amount
    if (previousAmount !== null && previousAmount !== undefined) {
      if (parts.length > 0) parts.push(" | ");
      parts.push(
        <>
          {t("common.previous")} {t("common.amount")}:{" "}
          <b>{formatCurrency(previousAmount)}</b>
        </>
      );
    }

    return {
      type: <>{getTranslatedEventTypeName(row.eventType, t)}</>,
      description: <>{parts}</>,
    };
  }
}
